import { useEffect, useState } from "react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { Recycle } from "lucide-react";
import { theme, card, emptyFrame, inputStyle } from "../theme";
import { IndexTabs } from "../components/Drawing";
import { useFrameworks } from "../context/FrameworksContext";
import { frameworkColor, frameworkLabel } from "../nav/frameworkMeta";
import { api } from "../api/client";

import { cbPalette, cbSafe } from "../lib/colourBlind";
// ── Static cross-standard mapping table ──────────────────────────────────────
// Each row represents a security topic and lists the corresponding control
// reference in each framework (null = not addressed by that framework).
// Only certifiable security / management-system standards belong here. Data
// protection laws (GDPR, UK GDPR, Jersey DPJL, ...) are legal obligations and
// are cross-referenced in DataProtectionMapping.tsx instead.
// The per-topic column maps further down are keyed by the `Topic` union derived
// from BASE_ROWS, so a key that matches no row is a compile error rather than a
// cell that silently never renders.

interface MappingRow {
  topic: string;
  category: string;
  iso27001: string | null;
  cyber_essentials: string | null;
  iso42001: string | null;
  iso9001: string | null;
  soc2: string | null;
  pci_dss: string | null;
  iasme_ca: string | null;
  nist_csf: string | null;
  iso45001: string | null;
}

const BASE_ROWS = [
  { topic: "Access Control Policy", category: "Access Control", iso27001: "A.5.15", cyber_essentials: "User access control", iso42001: "A.6.2", iso9001: null, soc2: "CC6.1", pci_dss: "Req 7" },
  { topic: "Multi-factor Authentication", category: "Access Control", iso27001: "A.8.5", cyber_essentials: "User access control (admin accounts)", iso42001: null, iso9001: null, soc2: "CC6.1", pci_dss: "Req 8.4" },
  { topic: "Privileged Access Management", category: "Access Control", iso27001: "A.5.18", cyber_essentials: "User access control (admin accounts)", iso42001: "A.6.2", iso9001: null, soc2: "CC6.3", pci_dss: "Req 7 & 8" },
  { topic: "Password / Credential Policy", category: "Access Control", iso27001: "A.5.17", cyber_essentials: "User access control", iso42001: null, iso9001: null, soc2: "CC6.1", pci_dss: "Req 8.3" },
  { topic: "Remote Access Security", category: "Access Control", iso27001: "A.6.7", cyber_essentials: "Boundary firewalls & internet gateways", iso42001: null, iso9001: null, soc2: "CC6.6", pci_dss: "Req 8" },
  { topic: "Firewall & Network Boundary", category: "Network Security", iso27001: "A.8.20", cyber_essentials: "Boundary firewalls & internet gateways", iso42001: null, iso9001: null, soc2: "CC6.6", pci_dss: "Req 1" },
  { topic: "Network Segmentation", category: "Network Security", iso27001: "A.8.22", cyber_essentials: "Boundary firewalls & internet gateways", iso42001: null, iso9001: null, soc2: "CC6.6", pci_dss: "Req 1.3" },
  { topic: "Malware / Antivirus Protection", category: "Endpoint Security", iso27001: "A.8.7", cyber_essentials: "Malware protection", iso42001: null, iso9001: null, soc2: "CC6.8", pci_dss: "Req 5" },
  { topic: "Patch Management & Updates", category: "Endpoint Security", iso27001: "A.8.19", cyber_essentials: "Patch management", iso42001: null, iso9001: null, soc2: "CC7.1", pci_dss: "Req 6.3" },
  { topic: "Secure Configuration", category: "Endpoint Security", iso27001: "A.8.9", cyber_essentials: "Secure configuration", iso42001: null, iso9001: null, soc2: "CC6.6", pci_dss: "Req 2" },
  { topic: "Mobile Device Management", category: "Endpoint Security", iso27001: "A.6.2", cyber_essentials: "User devices", iso42001: null, iso9001: null, soc2: "CC6.7", pci_dss: "Req 12.3" },
  { topic: "Cryptography & Encryption", category: "Data Protection", iso27001: "A.8.24", cyber_essentials: "Patch management (HTTPS)", iso42001: null, iso9001: null, soc2: "CC6.1", pci_dss: "Req 3 & 4" },
  { topic: "Data Classification", category: "Data Protection", iso27001: "A.5.12", cyber_essentials: null, iso42001: "A.8.2", iso9001: null, soc2: "CC6.1", pci_dss: "Req 9.5" },
  { topic: "Data Retention & Disposal", category: "Data Protection", iso27001: "A.8.10", cyber_essentials: null, iso42001: "A.8.3", iso9001: "7.5", soc2: "CC6.5", pci_dss: "Req 3.2" },
  { topic: "Backup & Recovery", category: "Business Continuity", iso27001: "A.8.13", cyber_essentials: null, iso42001: null, iso9001: "8.5.1", soc2: "A1.2", pci_dss: "Req 12.10" },
  { topic: "Business Continuity Plan", category: "Business Continuity", iso27001: "A.5.30", cyber_essentials: null, iso42001: null, iso9001: "8.5.1", soc2: "A1.3", pci_dss: "Req 12.10" },
  { topic: "Disaster Recovery", category: "Business Continuity", iso27001: "A.5.30", cyber_essentials: null, iso42001: null, iso9001: "8.5.1", soc2: "A1.3", pci_dss: "Req 12.10" },
  { topic: "Incident Detection & Response", category: "Incident Management", iso27001: "A.5.25", cyber_essentials: null, iso42001: "A.6.7", iso9001: "10.2", soc2: "CC7.3", pci_dss: "Req 12.10" },
  { topic: "Incident Reporting", category: "Incident Management", iso27001: "A.6.8", cyber_essentials: null, iso42001: "A.6.7", iso9001: "10.2", soc2: "CC7.3", pci_dss: "Req 12.10" },
  { topic: "Security Awareness Training", category: "People Security", iso27001: "A.6.3", cyber_essentials: null, iso42001: "A.4.2", iso9001: "7.2", soc2: "CC1.4", pci_dss: "Req 12.6" },
  { topic: "Supplier / Third-party Risk", category: "Supply Chain", iso27001: "A.5.19", cyber_essentials: null, iso42001: "A.5.5", iso9001: "8.4", soc2: "CC9.2", pci_dss: "Req 12.8" },
  { topic: "Software Development Security", category: "Application Security", iso27001: "A.8.25", cyber_essentials: "Patch management", iso42001: null, iso9001: "8.3", soc2: "CC8.1", pci_dss: "Req 6" },
  { topic: "Vulnerability Management", category: "Application Security", iso27001: "A.8.8", cyber_essentials: "Patch management", iso42001: null, iso9001: null, soc2: "CC7.1", pci_dss: "Req 11" },
  { topic: "Penetration Testing", category: "Application Security", iso27001: "A.8.8", cyber_essentials: null, iso42001: null, iso9001: null, soc2: "CC7.1", pci_dss: "Req 11.4" },
  { topic: "Audit Logging & Monitoring", category: "Monitoring", iso27001: "A.8.15", cyber_essentials: null, iso42001: "A.6.6", iso9001: "9.1", soc2: "CC7.2", pci_dss: "Req 10" },
  { topic: "Change Management", category: "Operations", iso27001: "A.8.32", cyber_essentials: null, iso42001: null, iso9001: "8.3", soc2: "CC8.1", pci_dss: "Req 6.5" },
  { topic: "Risk Assessment", category: "Governance", iso27001: "6.1.2", cyber_essentials: null, iso42001: "6.1.2", iso9001: "6.1", soc2: "CC3.1", pci_dss: "Req 12.3" },
  { topic: "Management Review", category: "Governance", iso27001: "9.3", cyber_essentials: null, iso42001: "9.3", iso9001: "9.3", soc2: "CC1.2", pci_dss: "Req 12.4" },
  { topic: "Internal Audit", category: "Governance", iso27001: "9.2", cyber_essentials: null, iso42001: "9.2", iso9001: "9.2", soc2: "CC4.1", pci_dss: "Req 12.4" },
  { topic: "Policy Management", category: "Governance", iso27001: "5.2", cyber_essentials: null, iso42001: "5.2", iso9001: "5.2", soc2: "CC1.3", pci_dss: "Req 12.1" },
  { topic: "Legal & Regulatory Requirements", category: "Governance", iso27001: "A.5.31", cyber_essentials: null, iso42001: "4.2", iso9001: "4.2", soc2: "CC3.1", pci_dss: null },
  { topic: "Asset Inventory", category: "Asset Management", iso27001: "A.5.9", cyber_essentials: "Patch management (scope)", iso42001: "A.8.1", iso9001: "7.1", soc2: "CC6.1", pci_dss: "Req 2.4" },
  { topic: "Physical Security", category: "Physical Security", iso27001: "A.7.1", cyber_essentials: null, iso42001: null, iso9001: "7.1", soc2: "CC6.4", pci_dss: "Req 9" },
  { topic: "AI System Risk Assessment", category: "AI Governance", iso27001: null, cyber_essentials: null, iso42001: "6.1.2", iso9001: null, soc2: null, pci_dss: null },
  { topic: "AI Transparency & Explainability", category: "AI Governance", iso27001: null, cyber_essentials: null, iso42001: "A.9.3", iso9001: null, soc2: null, pci_dss: null },
  { topic: "Customer Satisfaction Monitoring", category: "Quality", iso27001: null, cyber_essentials: null, iso42001: null, iso9001: "9.1.2", soc2: null, pci_dss: null },
  { topic: "Nonconformity & Corrective Action", category: "Quality", iso27001: "10.1", cyber_essentials: null, iso42001: "10.1", iso9001: "10.2", soc2: null, pci_dss: null },
] as const;

type Topic = (typeof BASE_ROWS)[number]["topic"];

// IASME CA theme references by topic. The 4 size tiers share the same 14 themes,
// so one column covers all tiers; unlisted topics are not addressed by IASME CA.
const IASME_CA_TOPIC_REFS: Partial<Record<Topic, string>> = {
  "Access Control Policy": "Theme 9",
  "Multi-factor Authentication": "Theme 9",
  "Privileged Access Management": "Theme 9",
  "Password / Credential Policy": "Theme 9",
  "Remote Access Security": "Theme 9",
  "Firewall & Network Boundary": "Theme 10",
  "Network Segmentation": "Theme 10",
  "Malware / Antivirus Protection": "Theme 10",
  "Patch Management & Updates": "Theme 12",
  "Secure Configuration": "Theme 10",
  "Mobile Device Management": "Theme 3",
  "Cryptography & Encryption": "Theme 3",
  "Data Classification": "Theme 3",
  "Data Retention & Disposal": "Theme 3",
  "Backup & Recovery": "Theme 13",
  "Business Continuity Plan": "Theme 14",
  "Disaster Recovery": "Theme 14",
  "Incident Detection & Response": "Theme 14",
  "Incident Reporting": "Theme 14",
  "Security Awareness Training": "Theme 7",
  "Supplier / Third-party Risk": "Theme 2 & 5",
  "Vulnerability Management": "Theme 12",
  "Penetration Testing": "Theme 12",
  "Audit Logging & Monitoring": "Theme 12",
  "Change Management": "Theme 11",
  "Risk Assessment": "Theme 5",
  "Management Review": "Theme 2",
  "Internal Audit": "Theme 12",
  "Policy Management": "Theme 8",
  "Legal & Regulatory Requirements": "Theme 4",
  "Asset Inventory": "Theme 3",
  "Physical Security": "Theme 6",
};

const NIST_CSF_TOPIC_REFS: Partial<Record<Topic, string>> = {
  "Access Control Policy": "PR.AA",
  "Multi-factor Authentication": "PR.AA",
  "Privileged Access Management": "PR.AA",
  "Password / Credential Policy": "PR.AA",
  "Remote Access Security": "PR.AA",
  "Firewall & Network Boundary": "PR.PS",
  "Network Segmentation": "PR.PS",
  "Malware / Antivirus Protection": "PR.PS",
  "Patch Management & Updates": "PR.PS",
  "Secure Configuration": "PR.PS",
  "Mobile Device Management": "PR.PS",
  "Cryptography & Encryption": "PR.DS",
  "Data Classification": "PR.DS",
  "Data Retention & Disposal": "PR.DS",
  "Backup & Recovery": "PR.IR",
  "Business Continuity Plan": "RC.RP",
  "Disaster Recovery": "RC.RP",
  "Incident Detection & Response": "RS.MA",
  "Incident Reporting": "RS.CO",
  "Security Awareness Training": "PR.AT",
  "Supplier / Third-party Risk": "GV.SC",
  "Software Development Security": "PR.PS",
  "Vulnerability Management": "DE.CM",
  "Penetration Testing": "DE.CM",
  "Audit Logging & Monitoring": "DE.CM",
  "Change Management": "PR.PS",
  "Risk Assessment": "ID.RA",
  "Management Review": "GV.OV",
  "Internal Audit": "GV.OV",
  "Policy Management": "GV.PO",
  "Legal & Regulatory Requirements": "GV.OC",
  "Asset Inventory": "ID.AM",
  "Physical Security": "PR.PS",
};

// ISO 45001 clause references by topic (refs match the seeded iso45001-* control IDs).
const ISO45001_TOPIC_REFS: Partial<Record<Topic, string>> = {
  "Access Control Policy":            "8.1",
  "Business Continuity Plan":         "8.2",
  "Incident Detection & Response":    "10.2",
  "Incident Reporting":               "10.2",
  "Security Awareness Training":      "7.2",
  "Supplier / Third-party Risk":      "8.1",
  "Audit Logging & Monitoring":       "9.1",
  "Risk Assessment":                  "6.1.2",
  "Management Review":                "9.3",
  "Internal Audit":                   "9.2",
  "Policy Management":                "5.2",
  "Legal & Regulatory Requirements":  "6.1.3",
  "Asset Inventory":                  "7.1",
  "Nonconformity & Corrective Action": "10.2",
};

const MAPPING_DATA: MappingRow[] = BASE_ROWS.map((row) => ({
  ...row,
  iasme_ca: IASME_CA_TOPIC_REFS[row.topic] ?? null,
  nist_csf: NIST_CSF_TOPIC_REFS[row.topic] ?? null,
  iso45001: ISO45001_TOPIC_REFS[row.topic] ?? null,
}));

// Each standard's identity colour comes from the one shared table (nav/frameworkMeta.ts), never a
// literal here. Tints are mixed from it rather than appending a hex alpha to a colour string.
const FRAMEWORKS = cbPalette([
  { id: "iso27001",        label: "ISO 27001",        short: "27001",   color: frameworkColor("iso27001") },
  { id: "cyber_essentials", label: "Cyber Essentials", short: "CE",     color: frameworkColor("cyber_essentials") },
  { id: "iso42001",        label: "ISO 42001",        short: "42001",   color: frameworkColor("iso42001") },
  { id: "iso9001",         label: "ISO 9001",         short: "9001",    color: frameworkColor("iso9001") },
  { id: "soc2",            label: "SOC 2",            short: "SOC 2",   color: frameworkColor("soc2") },
  { id: "pci_dss",         label: "PCI DSS",          short: "PCI",     color: frameworkColor("pci_dss") },
  { id: "iasme_ca",        label: "IASME CA",         short: "IASME",   color: frameworkColor("iasme_ca_small") },
  { id: "nist_csf",        label: "NIST CSF",         short: "CSF",     color: frameworkColor("nist_csf") },
  { id: "iso45001",        label: "ISO 45001",        short: "45001",   color: frameworkColor("iso45001") },
] as const);

/** A tint of a standard's colour at `pct` per cent strength. */
const tint = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

type FwId = "iso27001" | "cyber_essentials" | "iso42001" | "iso9001" | "soc2" | "pci_dss" | "iasme_ca" | "nist_csf" | "iso45001";

const IASME_TIER_IDS = ["iasme_ca_sole_trader", "iasme_ca_micro", "iasme_ca_small", "iasme_ca_medium_large"] as const;

const IASME_TIER_ROUTES: Record<string, string> = {
  iasme_ca_sole_trader:  "/iasme-ca-sole-trader",
  iasme_ca_micro:        "/iasme-ca-micro",
  iasme_ca_small:        "/iasme-ca-small",
  iasme_ca_medium_large: "/iasme-ca-medium-large",
};

const CATEGORIES = [...new Set(MAPPING_DATA.map((r) => r.category))];

interface SoaCounts {
  compliant: number;
  inProgress: number;
  gap: number;
  notStarted: number;
  total: number;
}

// Per-control SOA status map: `frameworkId:ref` → status string
type SoaStatusMap = Map<string, string>;

// Build key for lookup: normalise frameworkId + ref to match seeded control IDs.
// Seeded IDs are like "iso27001-a.5.15", mapping refs are like "A.5.15".
function soaKey(fwId: string, ref: string): string {
  return `${fwId}:${ref.toLowerCase()}`;
}

const STATUS_DOT: Record<string, { bg: string; title: string }> = cbPalette({
  COMPLIANT:    { bg: "#22c55e", title: "Compliant" },
  IN_PROGRESS:  { bg: "#eab308", title: "In progress" },
  GAP:          { bg: "#ef4444", title: "Gap" },
  NOT_STARTED:  { bg: "var(--c-border)", title: "Not started" },
});

// Maps a framework ID + control ref to the assessment page where it lives.
function controlMappingRoute(fwId: string, ref: string | null, iasmeTier: string | null = null): string {
  if (fwId === "iso27001") return ref?.startsWith("A.") ? "/annex-a" : "/mandatory-clauses";
  if (fwId === "cyber_essentials") return "/cyber-essentials";
  if (fwId === "iso42001") return ref?.startsWith("A.") ? "/iso42002" : "/iso42001";
  if (fwId === "iso9001") return "/iso9001";
  if (fwId === "soc2") return "/soc2";
  if (fwId === "pci_dss") return "/pci-dss/saq-a";
  if (fwId === "iasme_ca") return (iasmeTier && IASME_TIER_ROUTES[iasmeTier]) || "/dashboard";
  if (fwId === "nist_csf") return "/nist-csf";
  if (fwId === "iso45001") return "/iso45001";
  return "/dashboard";
}

export function ControlMappingPage() {
  const { enrolled } = useFrameworks();
  // Inside the consultant's client portal, navigation is page state rather than a URL.
  const navigate = usePortalNavigate();
  // The 4 IASME CA size tiers share one column; enrolment in any tier lights it up.
  const iasmeTier = IASME_TIER_IDS.find((t) => enrolled.has(t)) ?? null;
  const isFwEnrolled = (id: string) => {
    if (id === "iasme_ca") return iasmeTier !== null;
    if (id === "cyber_essentials") return enrolled.has("cyber_essentials") || enrolled.has("cyber_essentials_plus");
    return enrolled.has(id as never);
  };
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [highlightEnrolled, setHighlightEnrolled] = useState(true);
  const [soaByFramework, setSoaByFramework] = useState<Record<string, SoaCounts>>({});
  const [soaStatusMap, setSoaStatusMap] = useState<SoaStatusMap>(new Map());
  const [activeTab, setActiveTab] = useState<"table" | "reuse" | "pairs">("table");
  // Approved control-to-control mappings between the organisation's own standards (platform
  // reference data, GET /control-mappings/summary). Null until it answers; [] when none join.
  const [pairs, setPairs] = useState<MappingPair[] | null>(null);
  useEffect(() => {
    api.get<{ pairs: MappingPair[] }>("/control-mappings/summary")
      .then((d) => setPairs(d.pairs ?? []))
      .catch(() => setPairs([]));
  }, []);

  useEffect(() => {
    api.get<{ soa: Array<{ control: { id: string; frameworkId: string }; entry: { status: string; applicable?: boolean | null } }> }>("/controls/soa")
      .then(({ soa }) => {
        const counts: Record<string, SoaCounts> = {};
        const statusMap: SoaStatusMap = new Map();

        for (const row of soa) {
          // All 4 IASME CA size tiers roll up into the shared iasme_ca column.
          const fw = row.control.frameworkId.startsWith("iasme_ca_") ? "iasme_ca" : row.control.frameworkId;
          if (!counts[fw]) counts[fw] = { compliant: 0, inProgress: 0, gap: 0, notStarted: 0, total: 0 };
          // Not-applicable rows are out of the score, as in the platform's own (lib/frameworkScores.ts),
          // so a card reads the same percentage as the sidebar.
          const applies = row.entry.applicable !== false;
          if (applies) counts[fw].total++;
          const s = row.entry.status;
          if (!applies)                 { /* excluded: not counted */ }
          else if (s === "COMPLIANT")   counts[fw].compliant++;
          else if (s === "IN_PROGRESS") counts[fw].inProgress++;
          else if (s === "GAP")         counts[fw].gap++;
          else                          counts[fw].notStarted++;

          // Build per-cell status map: "frameworkId:ref" → status
          // Control IDs look like "iso27001-a.5.15"; strip the frameworkId prefix
          // to get the ref, then normalise case to match mapping table refs.
          const prefix = row.control.frameworkId + "-";
          const ref = row.control.id.startsWith(prefix)
            ? row.control.id.slice(prefix.length)
            : row.control.id;
          statusMap.set(soaKey(fw, ref), s);
        }

        setSoaByFramework(counts);
        setSoaStatusMap(statusMap);
      })
      .catch(() => {});
  }, []);

  // Derive reuse opportunities from already-loaded SOA state.
  // A row qualifies when ≥2 enrolled frameworks address the same topic AND
  // at least one is COMPLIANT while at least one other is GAP/NOT_STARTED.
  const reuseOpportunities = MAPPING_DATA.flatMap((row) => {
    const enrolledFws = FRAMEWORKS.filter((fw) => isFwEnrolled(fw.id) && row[fw.id as FwId] !== null);
    if (enrolledFws.length < 2) return [];
    const compliantFws = enrolledFws.filter((fw) => soaStatusMap.get(soaKey(fw.id, row[fw.id as FwId]!)) === "COMPLIANT");
    const gapFws = enrolledFws.filter((fw) => {
      const s = soaStatusMap.get(soaKey(fw.id, row[fw.id as FwId]!));
      return s === "GAP" || s === "NOT_STARTED";
    });
    if (compliantFws.length === 0 || gapFws.length === 0) return [];
    return [{ row, compliantFws, gapFws }];
  });

  const enrolledCount = FRAMEWORKS.filter((fw) => isFwEnrolled(fw.id)).length;

  const searchLower = search.toLowerCase();
  const visible = MAPPING_DATA.filter((row) => {
    if (categoryFilter !== "all" && row.category !== categoryFilter) return false;
    if (search && !row.topic.toLowerCase().includes(searchLower) && !row.category.toLowerCase().includes(searchLower)) return false;
    return true;
  });

  const coverageCount = (fwId: FwId) => MAPPING_DATA.filter((r) => r[fwId] !== null).length;

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Cross-Standard Control Mapping
      </h1>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: theme.color.subtle }}>
        See how security topics map across your enrolled frameworks. Where multiple standards apply to the same
        topic, evidence gathered for one often satisfies the others — reducing duplication.
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: theme.color.subtle }}>
        Data protection laws (GDPR, UK GDPR, Jersey DPJL and others) are legal obligations rather than security
        standards, so they are cross-referenced on the{" "}
        <button
          onClick={() => navigate("/data-protection-mapping")}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: theme.color.accent, fontSize: 12, fontWeight: 600, textDecoration: "underline" }}
        >
          Privacy by article
        </button>{" "}
        page instead.
      </p>

      {/* Summary stat — clickable shortcut to Evidence Reuse tab */}
      {reuseOpportunities.length > 0 && (
        <button
          onClick={() => setActiveTab("reuse")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            padding: "7px 14px",
            borderRadius: 3,
            border: `1px solid ${theme.color.successText}44`,
            background: theme.color.successText + "0f",
            color: theme.color.successText,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <Recycle size={14} style={{ flexShrink: 0 }} />
          {reuseOpportunities.length} potential evidence reuse{" "}
          {reuseOpportunities.length === 1 ? "opportunity" : "opportunities"} across{" "}
          {enrolledCount} enrolled {enrolledCount === 1 ? "framework" : "frameworks"} — view Evidence Reuse tab →
        </button>
      )}

      {/* Framework coverage summary */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {FRAMEWORKS.map((fw) => {
          const isEnrolled = isFwEnrolled(fw.id);
          const count = coverageCount(fw.id as FwId);
          return (
            <div
              key={fw.id}
              style={{
                ...card,
                flex: "0 0 auto",
                padding: "10px 16px",
                borderColor: isEnrolled ? fw.color : theme.color.border,
                opacity: highlightEnrolled && !isEnrolled ? 0.45 : 1,
                minWidth: 130,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: fw.color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                {fw.label}
                {isEnrolled && (
                  <span style={{ marginLeft: 6, fontSize: 9, background: tint(fw.color, 13), padding: "1px 5px", borderRadius: 4 }}>
                    enrolled
                  </span>
                )}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.heading }}>{count}</div>
              <div style={{ fontSize: 10, color: theme.color.subtle }}>topics addressed</div>
              {isEnrolled && soaByFramework[fw.id] && (() => {
                const sc = soaByFramework[fw.id];
                const pct = sc.total > 0 ? Math.round((sc.compliant / sc.total) * 100) : 0;
                return (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", height: 4, borderRadius: 3, overflow: "hidden", gap: 1, marginBottom: 4 }}>
                      {sc.compliant > 0  && <div style={{ flex: sc.compliant,  background: theme.color.successText, opacity: 0.85 }} />}
                      {sc.inProgress > 0 && <div style={{ flex: sc.inProgress, background: theme.color.warningText, opacity: 0.85 }} />}
                      {sc.gap > 0        && <div style={{ flex: sc.gap,        background: theme.color.dangerText,  opacity: 0.85 }} />}
                      {sc.notStarted > 0 && <div style={{ flex: sc.notStarted, background: theme.color.border }} />}
                    </div>
                    <div style={{ fontSize: 9, color: theme.color.muted }}>{pct}% compliant · {sc.gap} gap{sc.gap !== 1 ? "s" : ""}</div>
                  </div>
                );
              })()}
            </div>
          );
        })}
        {/* Every other enrolled standard with requirement rows (NCSC CAF, ISO 14001, ...): its score,
            the same as the sidebar's, though the topic table below does not map it yet. */}
        {[...enrolled]
          .filter((id) => !FRAMEWORKS.some((fw) => fw.id === id) && !id.startsWith("iasme_ca_") && id !== "cyber_essentials_plus" && soaByFramework[id])
          .map((id) => {
            const sc = soaByFramework[id];
            const pct = sc.total > 0 ? Math.round((sc.compliant / sc.total) * 100) : 0;
            const color = frameworkColor(id);
            return (
              <div key={id} style={{ ...card, flex: "0 0 auto", padding: "10px 16px", borderColor: color, minWidth: 130 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  {frameworkLabel(id)}
                  <span style={{ marginLeft: 6, fontSize: 9, background: tint(color, 13), padding: "1px 5px", borderRadius: 4 }}>enrolled</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.heading }}>{pct}%</div>
                <div style={{ fontSize: 10, color: theme.color.subtle }}>compliant · {sc.gap} gap{sc.gap !== 1 ? "s" : ""}</div>
                <div style={{ fontSize: 9, color: theme.color.muted, marginTop: 4 }}>Not in the topic table yet</div>
              </div>
            );
          })}
      </div>

      {/* Tab selector */}
      <IndexTabs
        items={[
          { id: "table", label: "Mapping Table" },
          { id: "reuse", label: "Evidence Reuse", count: reuseOpportunities.length > 0 ? reuseOpportunities.length : undefined },
          { id: "pairs", label: "Mapped controls", count: pairs && pairs.length > 0 ? pairs.reduce((n, p) => n + p.total, 0) : undefined },
        ]}
        value={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 18 }}
      />

      {activeTab === "table" && (<>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Search topics…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, width: 220, fontSize: 13 }}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ ...inputStyle, fontSize: 13 }}
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.color.subtle, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={highlightEnrolled}
            onChange={(e) => setHighlightEnrolled(e.target.checked)}
          />
          Dim unenrolled frameworks
        </label>
        <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
          {visible.length} of {MAPPING_DATA.length} topics
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", ...card, padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--c-panelAlt)", borderBottom: "1px solid var(--c-line)" }}>
              <th style={{ padding: "10px 14px", textAlign: "left", color: theme.color.heading, fontWeight: 700, whiteSpace: "nowrap", minWidth: 200 }}>
                Topic
              </th>
              <th style={{ padding: "10px 10px", textAlign: "left", color: theme.color.subtle, fontWeight: 600, whiteSpace: "nowrap", fontSize: 11 }}>
                Category
              </th>
              {FRAMEWORKS.map((fw) => {
                const isEnrolled = isFwEnrolled(fw.id);
                return (
                  <th
                    key={fw.id}
                    style={{
                      padding: "10px 8px",
                      textAlign: "center",
                      color: isEnrolled ? fw.color : theme.color.faint,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      fontSize: 11,
                      opacity: highlightEnrolled && !isEnrolled ? 0.45 : 1,
                    }}
                  >
                    {fw.short}
                  </th>
                );
              })}
              <th style={{ padding: "10px 8px", textAlign: "center", color: theme.color.subtle, fontWeight: 600, fontSize: 11 }}>
                Coverage
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row, idx) => {
              const refs = FRAMEWORKS.map((fw) => row[fw.id as FwId]);
              const covered = refs.filter((r) => r !== null).length;
              const enrolledCoveredCount = FRAMEWORKS.filter(
                (fw) => isFwEnrolled(fw.id) && row[fw.id as FwId] !== null
              ).length;
              return (
                <tr
                  key={idx}
                  style={{
                    borderBottom: `1px solid ${theme.color.border}`,
                    background: enrolledCoveredCount > 1 ? "var(--c-accentBg)" : "transparent",
                  }}
                >
                  <td style={{ padding: "9px 14px", color: theme.color.heading, fontWeight: 500 }}>
                    {row.topic}
                  </td>
                  <td style={{ padding: "9px 10px", color: theme.color.subtle, fontSize: 11 }}>
                    {row.category}
                  </td>
                  {FRAMEWORKS.map((fw) => {
                    const ref = row[fw.id as FwId];
                    const isEnrolled = isFwEnrolled(fw.id);
                    return (
                      <td
                        key={fw.id}
                        title={ref ? `${fw.label}: ${ref}` : `Not addressed by ${fw.label}`}
                        style={{
                          padding: "9px 8px",
                          textAlign: "center",
                          opacity: highlightEnrolled && !isEnrolled ? 0.35 : 1,
                        }}
                      >
                        {ref ? (() => {
                          const cellStatus = isEnrolled ? soaStatusMap.get(soaKey(fw.id, ref)) : undefined;
                          const dot = cellStatus ? STATUS_DOT[cellStatus] : null;
                          const chip = (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: tint(fw.color, 13), color: fw.color, whiteSpace: "nowrap" }}>
                              {dot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot.bg, flexShrink: 0 }} title={dot.title} />}
                              {ref}
                            </span>
                          );
                          return isEnrolled ? (
                            <button
                              onClick={() => navigate(controlMappingRoute(fw.id, ref, iasmeTier))}
                              title={`${fw.label}: ${ref}${dot ? ` — ${dot.title}` : ""}`}
                              style={{ display: "inline-flex", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textDecorationStyle: "dotted" }}
                            >
                              {chip}
                            </button>
                          ) : chip;
                        })() : (
                          <span style={{ color: theme.color.faint, fontSize: 14 }}>·</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: "9px 8px", textAlign: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: covered >= 4 ? cbSafe("#dcfce7") : covered >= 2 ? "#fef3c7" : "var(--c-panelAlt)",
                        color: covered >= 4 ? cbSafe("#15803d") : covered >= 2 ? "#92400e" : theme.color.faint,
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: "22px",
                        textAlign: "center",
                      }}
                    >
                      {covered}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {visible.length === 0 && (
          <div style={{ padding: "28px 20px", textAlign: "center", color: theme.color.subtle, fontSize: 13 }}>
            No topics match your search.
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ marginTop: 14, display: "flex", gap: 20, fontSize: 11, color: theme.color.subtle, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: cbSafe("#dcfce7"), display: "inline-block" }} />
          Highlighted row = 2+ of your enrolled frameworks share this topic (evidence reuse opportunity)
        </span>
        <span>· = not addressed</span>
        <span>Coverage = total frameworks that address the topic</span>
      </div>

      </>)}

      {/* Evidence Reuse panel */}
      {activeTab === "reuse" && (
        <div>
          {reuseOpportunities.length === 0 ? (
            <div
              style={{
                ...emptyFrame,
                padding: "36px 24px",
                color: theme.color.subtle,
                fontSize: 13,
              }}
            >
              No reuse opportunities found yet — complete more controls to see where evidence can be shared.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {reuseOpportunities.map(({ row, compliantFws, gapFws }, idx) => {
                // Build a concise reuse note.
                const firstCompliant = compliantFws[0];
                const firstCompliantRef = row[firstCompliant.id as FwId]!;
                const firstGap = gapFws[0];
                const firstGapRef = row[firstGap.id as FwId]!;
                const reuseNote = `Evidence for ${firstCompliant.label} ${firstCompliantRef} likely satisfies ${firstGap.label} ${firstGapRef} — review before duplicating effort.`;

                return (
                  <div key={idx} style={{ ...card, padding: "16px 20px" }}>
                    {/* Topic heading + category badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>{row.topic}</span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 3,
                          background: "var(--c-panelAlt)",
                          color: theme.color.subtle,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}
                      >
                        {row.category}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
                      {/* Already compliant */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.successText, marginBottom: 6 }}>
                          ✓ Already compliant in:
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {compliantFws.map((fw) => {
                            const ref = row[fw.id as FwId]!;
                            return (
                              <button
                                key={fw.id}
                                onClick={() => navigate(controlMappingRoute(fw.id, ref, iasmeTier))}
                                title={`${fw.label}: ${ref} — View control`}
                                style={{ display: "inline-flex", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textDecorationStyle: "dotted" }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "3px 8px",
                                    borderRadius: 3,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    background: tint(fw.color, 13),
                                    color: fw.color,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_DOT.COMPLIANT.bg, flexShrink: 0 }} />
                                  {fw.short} {ref}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Can satisfy */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.warningText, marginBottom: 6 }}>
                          → Can satisfy:
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {gapFws.map((fw) => {
                            const ref = row[fw.id as FwId]!;
                            const status = soaStatusMap.get(soaKey(fw.id, ref)) ?? "NOT_STARTED";
                            const dot = STATUS_DOT[status] ?? STATUS_DOT.NOT_STARTED;
                            return (
                              <span
                                key={fw.id}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: "3px 8px",
                                  borderRadius: 3,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: tint(fw.color, 13),
                                  color: fw.color,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot.bg, flexShrink: 0 }} title={dot.title} />
                                {fw.short} {ref}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Reuse note */}
                    <div
                      style={{
                        fontSize: 12,
                        color: theme.color.subtle,
                        fontStyle: "italic",
                        marginBottom: 12,
                        paddingLeft: 10,
                        borderLeft: `3px solid ${theme.color.border}`,
                      }}
                    >
                      {reuseNote}
                    </div>

                    {/* View control buttons for gap frameworks */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {gapFws.map((fw) => {
                        const ref = row[fw.id as FwId]!;
                        return (
                          <button
                            key={fw.id}
                            onClick={() => navigate(controlMappingRoute(fw.id, ref, iasmeTier))}
                            style={{
                              padding: "5px 12px",
                              borderRadius: 3,
                              border: `1px solid ${tint(fw.color, 33)}`,
                              background: "transparent",
                              color: fw.color,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            View {fw.short} {ref} →
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Approved control mappings between the organisation's own standards */}
      {activeTab === "pairs" && <MappedPairsPanel pairs={pairs} />}
    </div>
  );
}

// ── Mapped controls: how many approved mappings join each pair of standards ──

interface MappingPair {
  a: string;
  b: string;
  aLabel: string;
  bLabel: string;
  total: number;
  equivalent: number;
  partial: number;
  related: number;
}

function MappedPairsPanel({ pairs }: { pairs: MappingPair[] | null }) {
  const th: React.CSSProperties = { textAlign: "left", padding: "8px 12px", fontSize: 12, fontWeight: 600, color: theme.color.muted, borderBottom: `1px solid ${theme.color.line}` };
  const td: React.CSSProperties = { padding: "8px 12px", fontSize: 13, color: theme.color.text, borderBottom: `1px solid ${theme.color.border}`, fontVariantNumeric: "tabular-nums" };
  return (
    <div>
      <div style={{ ...card, padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Do the work once</div>
        <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.55 }}>
          One set of policies, one evidence repository and one set of registers serve every standard you hold.
          The mappings below show where evidence already accepted for a control in one standard is worth showing
          for a control in another. Each control is still judged on its own requirement: a mapping says where to
          look first, never that the second control is met.
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 8 }}>
          Equivalent: the two controls ask for the same thing. Partial: one covers part of the other. Related: they
          touch the same subject. Open a control on its standard's page to see what it maps to.
        </div>
      </div>
      {pairs === null ? (
        <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading…</div>
      ) : pairs.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "36px 24px", color: theme.color.subtle, fontSize: 13 }}>
          No approved mappings join your standards yet. They appear here once you hold two standards the platform has mapped.
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Standard</th>
                <th style={th}>Mapped to</th>
                <th style={{ ...th, textAlign: "right" }}>Equivalent</th>
                <th style={{ ...th, textAlign: "right" }}>Partial</th>
                <th style={{ ...th, textAlign: "right" }}>Related</th>
                <th style={{ ...th, textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pairs.map((p) => (
                <tr key={`${p.a}:${p.b}`}>
                  <td style={{ ...td, fontWeight: 600, color: theme.color.heading }}>{p.aLabel}</td>
                  <td style={{ ...td, fontWeight: 600, color: theme.color.heading }}>{p.bLabel}</td>
                  <td style={{ ...td, textAlign: "right" }}>{p.equivalent}</td>
                  <td style={{ ...td, textAlign: "right" }}>{p.partial}</td>
                  <td style={{ ...td, textAlign: "right" }}>{p.related}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{p.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
